const BACKEND_URI = "";

import mkdocsConfig from "../mkdocsConfig.generated.json";

import {
    AccessSummary,
    ChatAppResponse,
    ChatAppResponseOrError,
    ChatAppRequest,
    Config,
    SimpleAPIResponse,
    HistoryListApiResponse,
    HistoryApiResponse,
    Feedback
} from "./models";
import { useLogin, getToken } from "../authConfig";

export async function getHeaders(idToken: string | undefined): Promise<Record<string, string>> {
    // Add the Bearer token when login is enabled and a token is available
    if (useLogin) {
        if (idToken) {
            return { Authorization: `Bearer ${idToken}` };
        }
    }

    return {};
}

export async function configApi(): Promise<Config> {
    const response = await fetch(`${BACKEND_URI}/config`, {
        method: "GET"
    });

    return (await response.json()) as Config;
}

export async function accessApi(idToken: string | undefined): Promise<AccessSummary> {
    const response = await fetch(`${BACKEND_URI}/access`, { method: "GET", headers: await getHeaders(idToken) });
    if (!response.ok) {
        return { documentation: false, apps: [] };
    }
    return (await response.json()) as AccessSummary;
}

export async function chatApi(request: ChatAppRequest, shouldStream: boolean, idToken: string | undefined, signal: AbortSignal): Promise<Response> {
    let url = `${BACKEND_URI}/chat`;
    if (shouldStream) {
        url += "/stream";
    }
    const headers = await getHeaders(idToken);
    return await fetch(url, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(request),
        signal: signal
    });
}

export async function submitFeedbackApi(feedback: Feedback, idToken: string | undefined): Promise<boolean> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`${BACKEND_URI}/feedback`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(feedback)
    });
    return response.ok;
}

export async function getSpeechApi(text: string): Promise<string | null> {
    return await fetch("/speech", {
        method: "POST",
        headers: {
            "Content-Type": "application/json"
        },
        body: JSON.stringify({
            text: text
        })
    })
        .then(response => {
            if (response.status == 200) {
                return response.blob();
            } else if (response.status == 400) {
                console.log("Speech synthesis is not enabled.");
                return null;
            } else {
                console.error("Unable to get speech synthesis.");
                return null;
            }
        })
        .then(blob => (blob ? URL.createObjectURL(blob) : null));
}

export function getCitationFilePath(citation: string): string {
    // If there are parentheses at end of citation, remove part in parentheses
    const cleanedCitation = citation.replace(/\s*\(.*?\)\s*$/, "").trim();
    return `${BACKEND_URI}/content/${cleanedCitation}`;
}

/**
 * Convert a citation URL (e.g. "/content/guidelines__branching-strategy.md")
 * to a relative MkDocs path (e.g. "guidelines/branching-strategy/").
 * Returns the path segment or null if the citation is not a .md file
 * or if the file is in a section excluded from MkDocs rendering.
 */
// Generated by scripts/sync_nextdoc.py from the VEMB mkdocs.yml
const MKDOCS_EXCLUDED_PREFIXES = mkdocsConfig.excludedPrefixes;
const MKDOCS_KNOWN_MARKDOWN_FILES = new Set(mkdocsConfig.knownMarkdownFiles ?? []);
const VEMB_REPO_BLOB_BASE = mkdocsConfig.sourceRepoBlobBase;

export function citationToMkDocsPath(citation: string): string | null {
    // Extract filename from /content/<filename> URL
    const contentPrefix = "/content/";
    const idx = citation.lastIndexOf(contentPrefix);
    const filename = idx >= 0 ? citation.substring(idx + contentPrefix.length) : citation;

    if (!filename.endsWith(".md")) return null;

    // Avoid broken navigation for stale or unknown citations that no longer exist in MkDocs
    if (MKDOCS_KNOWN_MARKDOWN_FILES.size > 0 && !MKDOCS_KNOWN_MARKDOWN_FILES.has(filename)) {
        return null;
    }

    // Convert: double underscores → slashes, strip .md, encode each segment, append trailing slash
    let path = filename
        .replace(/\.md$/, "")
        .replace(/__/g, "/")
        .split("/")
        .map(segment => encodeURIComponent(segment))
        .join("/");

    // MkDocs with use_directory_urls=true serves index.md as the parent folder
    // e.g. nextdev/index → nextdev/, tutorials/index → tutorials/
    path = path.replace(/\/index$/i, "");
    // Root index.md should navigate to the MkDocs site root.
    if (path.toLowerCase() == "index") {
        path = "";
    }
    const result = path ? path + "/" : "./";

    // Don't navigate to excluded sections — they don't exist in MkDocs
    if (MKDOCS_EXCLUDED_PREFIXES.some(prefix => result.startsWith(prefix))) {
        return null;
    }

    return result;
}

/**
 * Resolve a relative markdown link (e.g. "./../community/index.md") to a flattened NextDoc
 * file name ("community__index.md"). With baseContentName (the flattened name of the file that
 * contains the link, e.g. "nextdev__index.md") the link is resolved exactly; without it the
 * link is resolved from the docs root. The result must be a known MkDocs file, otherwise null
 * is returned and the caller must not render a clickable link.
 */
export function docLinkToContentName(href: string, baseContentName?: string): string | null {
    const path = href.split("#")[0].split("?")[0];
    if (!path.endsWith(".md")) return null;
    const resolved = baseContentName ? baseContentName.replace(/\.md$/, "").split("__").slice(0, -1) : [];
    for (const part of path.split("/")) {
        if (part === "..") resolved.pop();
        else if (part !== "." && part !== "") resolved.push(part);
    }
    const flat = resolved.join("__");
    return MKDOCS_KNOWN_MARKDOWN_FILES.has(flat) ? flat : null;
}

/**
 * Build a source repository URL for markdown citations that are excluded from MkDocs pages.
 * Returns null when the citation is not a markdown file or not in an excluded section.
 */
export function citationToSourceRepoUrl(citation: string): string | null {
    const contentPrefix = "/content/";
    const idx = citation.lastIndexOf(contentPrefix);
    const filename = idx >= 0 ? citation.substring(idx + contentPrefix.length) : citation;

    if (!filename.endsWith(".md")) return null;

    const repoPath = filename.replace(/__/g, "/");
    if (!MKDOCS_EXCLUDED_PREFIXES.some(prefix => repoPath.startsWith(prefix))) return null;

    return `${VEMB_REPO_BLOB_BASE}${repoPath}`;
}

/** URL of the rendered NextDoc page for a markdown citation, or its source file if MkDocs does not serve it. */
export function citationToNextDocUrl(citation: string): string | null {
    const mkdocsPath = citationToMkDocsPath(citation);
    if (mkdocsPath !== null) {
        return mkdocsConfig.siteUrl + (mkdocsPath === "./" ? "" : mkdocsPath);
    }
    return citationToSourceRepoUrl(citation);
}

export async function uploadFileApi(request: FormData, idToken: string): Promise<SimpleAPIResponse> {
    const response = await fetch("/upload", {
        method: "POST",
        headers: await getHeaders(idToken),
        body: request
    });

    if (!response.ok) {
        throw new Error(`Uploading files failed: ${response.statusText}`);
    }

    const dataResponse: SimpleAPIResponse = await response.json();
    return dataResponse;
}

export async function deleteUploadedFileApi(filename: string, idToken: string): Promise<SimpleAPIResponse> {
    const headers = await getHeaders(idToken);
    const response = await fetch("/delete_uploaded", {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ filename })
    });

    if (!response.ok) {
        throw new Error(`Deleting file failed: ${response.statusText}`);
    }

    const dataResponse: SimpleAPIResponse = await response.json();
    return dataResponse;
}

export async function listUploadedFilesApi(idToken: string): Promise<string[]> {
    const response = await fetch(`/list_uploaded`, {
        method: "GET",
        headers: await getHeaders(idToken)
    });

    if (!response.ok) {
        throw new Error(`Listing files failed: ${response.statusText}`);
    }

    const dataResponse: string[] = await response.json();
    return dataResponse;
}

export async function postChatHistoryApi(item: any, idToken: string): Promise<any> {
    const headers = await getHeaders(idToken);
    const response = await fetch("/chat_history", {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(item)
    });

    if (!response.ok) {
        throw new Error(`Posting chat history failed: ${response.statusText}`);
    }

    const dataResponse: any = await response.json();
    return dataResponse;
}

export async function getChatHistoryListApi(count: number, continuationToken: string | undefined, idToken: string): Promise<HistoryListApiResponse> {
    const headers = await getHeaders(idToken);
    let url = `${BACKEND_URI}/chat_history/sessions?count=${count}`;
    if (continuationToken) {
        url += `&continuationToken=${continuationToken}`;
    }

    const response = await fetch(url.toString(), {
        method: "GET",
        headers: { ...headers, "Content-Type": "application/json" }
    });

    if (!response.ok) {
        throw new Error(`Getting chat histories failed: ${response.statusText}`);
    }

    const dataResponse: HistoryListApiResponse = await response.json();
    return dataResponse;
}

export async function getChatHistoryApi(id: string, idToken: string): Promise<HistoryApiResponse> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`/chat_history/sessions/${id}`, {
        method: "GET",
        headers: { ...headers, "Content-Type": "application/json" }
    });

    if (!response.ok) {
        throw new Error(`Getting chat history failed: ${response.statusText}`);
    }

    const dataResponse: HistoryApiResponse = await response.json();
    return dataResponse;
}

export async function deleteChatHistoryApi(id: string, idToken: string): Promise<any> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`/chat_history/sessions/${id}`, {
        method: "DELETE",
        headers: { ...headers, "Content-Type": "application/json" }
    });

    if (!response.ok) {
        throw new Error(`Deleting chat history failed: ${response.statusText}`);
    }
}
